import type { PermissionContextDto, RolePermissionsMatrixApiResponse } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { PERMISSIONS_SUPPORT_DIAGNOSTICS_MATRIX_ENTRIES } from "le-coffre-resources/dist/Notary/PermissionsSupportDiagnosticsMatrix";
import {
	evaluateRolePermissionMatrixTriState,
	rolePermissionMatrixTriStateFromTrace,
	traceRolePermissionMatrixEval,
	type RolePermissionsMatrixJson,
} from "le-coffre-resources/dist/Shared/rolePermissionsMatrixEvaluate";
import { isSuperAdminPermissionContext, resolveMatrixEvaluationRole } from "@Front/Utils/resolveMatrixEvaluationRole";

export type RolePermissionsMatrix = RolePermissionsMatrixApiResponse | null;

export interface RolePermissionCheckContext {
	resource: string;
	action: string;
	scopedEntity: string | null | undefined;
	matrix: RolePermissionsMatrix;
	permissionContext: PermissionContextDto | null;
}

/**
 * null : matrice / ressource / action absente (diagnostic) ; utile pour différencier « refus explicite » et « non présent dans la matrice filtrée ».
 */
export function checkPermissionTriState(ctx: RolePermissionCheckContext): boolean | null {
	if (isSuperAdminPermissionContext(ctx.permissionContext)) {
		return true;
	}

	const effectiveRole = resolveMatrixEvaluationRole(ctx.permissionContext);

	if (!effectiveRole || !ctx.matrix) {
		return null;
	}

	const baseParams = {
		matrix: ctx.matrix as RolePermissionsMatrixJson,
		resource: ctx.resource,
		action: ctx.action,
		scopedEntity: ctx.scopedEntity,
		mapHttpVerbsForAction: true,
		effectiveRole,
	} as const;

	const trace = traceRolePermissionMatrixEval({ ...baseParams });
	return rolePermissionMatrixTriStateFromTrace(trace, false);
}

/** Même comportement que l’ancien `checkPermission` du hook (null → false). */
export function checkPermissionInMatrix(ctx: RolePermissionCheckContext): boolean {
	const t = checkPermissionTriState(ctx);
	return t === true;
}

export type PermissionsSupportDiagnosticsSnapshot = Record<string, boolean | null>;

function fillDiagnosticsKeys(value: boolean | null): PermissionsSupportDiagnosticsSnapshot {
	const out: PermissionsSupportDiagnosticsSnapshot = {};
	for (const entry of PERMISSIONS_SUPPORT_DIAGNOSTICS_MATRIX_ENTRIES) {
		out[entry.key] = value;
	}
	return out;
}

export function computePermissionsSupportDiagnosticsSnapshot(
	matrix: RolePermissionsMatrix,
	permissionContext: PermissionContextDto | null,
): PermissionsSupportDiagnosticsSnapshot {
	const json = matrix as RolePermissionsMatrixJson;
	const effectiveRole = resolveMatrixEvaluationRole(permissionContext);
	if (!effectiveRole || !matrix) {
		return fillDiagnosticsKeys(null);
	}
	if (isSuperAdminPermissionContext(permissionContext)) {
		return fillDiagnosticsKeys(true);
	}
	const out: PermissionsSupportDiagnosticsSnapshot = {};
	for (const entry of PERMISSIONS_SUPPORT_DIAGNOSTICS_MATRIX_ENTRIES) {
		const scopedEntity = entry.scopeMode === "active_office" ? (permissionContext?.activeOfficeUid ?? null) : null;
		out[entry.key] = evaluateRolePermissionMatrixTriState({
			matrix: json,
			resource: entry.resource,
			action: entry.action,
			scopedEntity,
			mapHttpVerbsForAction: false,
			effectiveRole,
		});
	}
	return out;
}
